import React from 'react';

/**
 * Last-resort error boundary used to wrap booking surfaces.
 *
 * If a child component throws during render or in a lifecycle method,
 * we display a small inline fallback (instead of a blank white screen)
 * and log the error to the browser console for triage.
 *
 * Purposely lightweight: no API calls, no analytics, no styling beyond
 * inline minimal CSS — so it can never itself be the source of an error.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      '[ErrorBoundary] caught error in',
      this.props.label || 'unnamed boundary',
      error,
      info,
    );
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          data-testid="error-boundary-fallback"
          style={{
            padding: '24px',
            margin: '16px',
            border: '1px solid #fecaca',
            background: '#fef2f2',
            borderRadius: '8px',
            maxWidth: '480px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <h3 style={{ margin: '0 0 8px', color: '#991b1b', fontSize: '16px' }}>
            Something went wrong
          </h3>
          <p style={{ margin: '0 0 16px', color: '#7f1d1d', fontSize: '14px' }}>
            Please reload the page and try again. If the problem persists, contact support.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload-btn"
            style={{
              padding: '8px 16px',
              background: '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
